import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../../components/Loading';
import {
  seekerApi,
  errorMessage,
  changedRecently,
  relativeDay,
  type ApplicationCounts,
  type ApplicationGroup,
  type SeekerListItem,
} from '../../applications/applicationStatusApi';
import { GroupTabs, StatusBadge } from '../../applications/applicationUi';
import ScoutOffersSection from '../../applications/ScoutOffersSection';

// 구직자 지원현황 목록 (application-status-design.md 4장)
// - 4개 그룹 탭 + 건수, 마지막 상태 변화 순 정렬, 6시간 내 변화는 배경 강조
// - 행 우측 액션은 상태에 따라: 청빙 제안 → 응답하기, 연락받음·면접 → 메시지, 그 외 → 보기

function rowAction(item: SeekerListItem): { label: string; primary?: boolean } {
  if (item.status === 'OFFERED') return { label: '응답하기', primary: true };
  if (item.status === 'CONTACTED' || item.status === 'INTERVIEW') return { label: '메시지' };
  return { label: '보기' };
}

function rowMeta(item: SeekerListItem) {
  const applied = `지원 ${item.appliedAt.slice(0, 10).replace(/-/g, '.')}`;
  if (item.note) return { text: `${applied} · ${item.note}`, warn: true };
  if (item.status === 'SUBMITTED') return { text: applied };
  return { text: `${applied} · ${relativeDay(item.statusChangedAt)} ${item.statusLabel}` };
}

export default function ApplicationManagePanel() {
  const navigate = useNavigate();
  const [group, setGroup] = useState<ApplicationGroup>('all');
  const [items, setItems] = useState<SeekerListItem[]>([]);
  const [counts, setCounts] = useState<ApplicationCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    seekerApi
      .list(group)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setCounts(data.counts);
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e, '지원 내역을 불러오지 못했습니다.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [group]);

  const openDetail = (id: number) => {
    navigate(`/mypage/applications/${id}`);
    window.scrollTo(0, 0);
  };

  const hasAny = (counts?.all ?? 0) > 0;

  return (
    <div className="subpage__main__content" style={{ marginTop: 18 }}>
      <ScoutOffersSection onAccepted={() => setGroup('all')} />
      {counts && hasAny && (
        <p style={{ color: 'var(--ink-3)', marginBottom: 4 }}>
          총 {counts.all}건 · 진행중 {counts.active}건
        </p>
      )}
      {hasAny && <GroupTabs value={group} counts={counts} onChange={setGroup} />}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <Loading />
        </div>
      ) : error ? (
        <div className="app-empty">
          <p>{error}</p>
        </div>
      ) : !hasAny ? (
        <div className="app-empty">
          <strong>아직 지원한 공고가 없습니다</strong>
          {/* 1차 버튼은 "조건 저장" — 저장하면 새 공고 알림의 사용자가 된다 (application-status-design.md 4.2 ⑤) */}
          <p>
            지역과 직분을 저장해 두면 새 공고가 올라올 때
            <br />
            알려드립니다.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="app-btn app-btn--primary" onClick={() => navigate('/mypage/resumemanage#job-alerts')}>
              조건 저장하고 알림 받기
            </button>
            <button type="button" className="app-btn" onClick={() => navigate('/recruit')}>
              공고 둘러보기
            </button>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="app-empty">
          <p>이 그룹에 해당하는 지원이 없습니다.</p>
        </div>
      ) : (
        <div className="app-list">
          {items.map((item) => {
            const action = rowAction(item);
            const meta = rowMeta(item);
            const fresh = item.status !== 'SUBMITTED' && changedRecently(item.statusChangedAt);
            return (
              <div
                key={item.id}
                className={`app-row${fresh ? ' is-fresh' : ''}${item.group === 'closed' ? ' is-closed' : ''}`}
                onClick={() => openDetail(item.id)}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="app-row__church">
                    {[item.job.church, item.job.region].filter(Boolean).join(' · ')}
                    {item.job.closed && ' · 마감'}
                  </div>
                  <div className="app-row__title">{item.job.title}</div>
                  <div className={`app-row__meta${meta.warn ? ' app-row__note' : ''}`}>{meta.text}</div>
                </div>
                <StatusBadge status={item.status} />
                <button
                  type="button"
                  className={`app-row__action${action.primary ? ' app-row__action--primary' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetail(item.id);
                  }}
                >
                  {action.label} ›
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
